"use client";

import React, { useCallback } from "react";
import { MODAL_IDS, useGlobalModalStack } from "@wtc/ui";
import { Message } from "@/lib/core/crm/types";
import { Client } from "@/lib/types/database";
import { useCompose } from "@/lib/hooks/compose/useCompose";
import { ComposeFormTabs } from "./ComposeFormTabs";
import { ComposeFormValues } from "@/lib/types/compose-modal-types";
import { EmailRecipient } from "../ClientEmailSelector";
import { ComposeProviderStack } from "@/lib/contexts/compose/ComposeProviders";
import {
  getFormStateValue,
  getRecipientsStateValue,
  getClientStateValue,
  getEmailFormatStateValue,
} from "./ComposeModalContextUtils";
import { useComposeDraftLifecycle } from "./useComposeDraftLifecycle";
import { useComposeSubmitResolver } from "./useComposeSubmitResolver";
import { useDeferredComposeBody } from "./useDeferredComposeBody";
import { TicketSummary } from "@/lib/types/database";
import type { ComposeDisclosureOptions } from "@/lib/types/compose-modal-types";

export type ComposeMessageFormValues = ComposeFormValues;

/**
 * Everything the render surface can supply, extracted from the old
 * `ComposeModalInner` so the compose UI is a single surface-agnostic
 * component. The modal wraps `renderShell` in a `ResponsiveModal`; inline
 * surfaces (e.g. the sequences/today composer pane) render it in a plain
 * container. All form logic — draft autosave, template resolution, signature,
 * variables — lives here so both surfaces share ONE code path.
 */
export interface ComposeFormProps {
  /** Whether the surface is "open" — drives useCompose seeding. Inline: true. */
  isOpen?: boolean;
  originalMessage?: Message | null;
  onSubmit: (values: ComposeMessageFormValues) => void;
  isSending: boolean;
  initialRecipient?: string;
  initialClient?: Client;
  initialRecipients?: EmailRecipient[];
  /** Addresses to pre-seed as BCC (e.g. a support inbox's group address). */
  initialBccRecipients?: EmailRecipient[];
  initialSubject?: string;
  initialBody?: string;
  selectedTicket?: TicketSummary | null;
  /** Called (after draft save) when the surface should close. Inline: unmount. */
  onOpenChange?: (open: boolean) => void;
  globalModalId?: string;
  isForwardMode?: boolean;
  /**
   * Defer the heavy body one frame (modal opening animation). Inline surfaces
   * are always mounted, so they render immediately (default false).
   */
  deferBody?: boolean;
  /** Show the "open full screen composer" action. Hidden on inline surfaces. */
  showFullScreenButton?: boolean;
  /** Progressive-disclosure config for narrow/inline surfaces. */
  disclosure?: ComposeDisclosureOptions;
  /**
   * Wraps the form output in the surface chrome. Receives the current
   * `hasUnfilledVariables` so a modal can surface it in its title/description.
   * Defaults to a plain flex container (inline).
   */
  renderShell?: (args: {
    children: React.ReactNode;
    hasUnfilledVariables: boolean;
    bodyShown: boolean;
  }) => React.ReactNode;
}

const defaultShell = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col">{children}</div>
);

/**
 * The compose surface itself: seeds the form state, wires the extracted
 * concerns together, and renders the tabs inside whatever shell the caller
 * supplies. The concerns it delegates:
 *
 * - `useComposeDraftLifecycle` — draft identity, save-on-close, full-screen handoff
 * - `useComposeSubmitResolver` — signature + template resolution before send
 * - `useDeferredComposeBody` — when the heavy body subtree may mount
 */
export const ComposeForm = React.memo(function ComposeForm(props: ComposeFormProps) {
  const {
    isOpen = true,
    originalMessage,
    onSubmit,
    isSending,
    initialRecipient,
    initialClient,
    initialRecipients,
    initialBccRecipients,
    initialSubject,
    initialBody,
    selectedTicket,
    onOpenChange,
    globalModalId = MODAL_IDS.messages.compose,
    isForwardMode = false,
    deferBody = false,
    showFullScreenButton = true,
    disclosure,
    renderShell = defaultShell,
  } = props;

  const { openModal } = useGlobalModalStack();

  // Use refactored useCompose hook
  const composeForm = useCompose({
    isOpen,
    originalMessage: isForwardMode || initialRecipients?.length ? null : originalMessage,
    initialRecipient,
    initialClient,
    initialRecipients,
    initialBccRecipients,
    initialSubject,
    initialBody,
    selectedTicket,
  });

  const { clearSavedDraft, handleOpenChange, handleGoToFullScreen } = useComposeDraftLifecycle({
    composeForm,
    originalMessage,
    initialRecipient,
    onOpenChange,
  });

  const handleSubmitResolved = useComposeSubmitResolver({
    composeForm,
    selectedTicket,
    onSubmit,
    clearSavedDraft,
  });

  const showBody = useDeferredComposeBody(isOpen, deferBody);

  const handleCreateTask = useCallback(() => {
    if (originalMessage) {
      openModal(MODAL_IDS.tasks.create, { message: originalMessage });
    }
  }, [originalMessage, openModal]);

  const handleCreateTicket = useCallback(() => {
    if (originalMessage) {
      openModal(MODAL_IDS.tickets.createQuick, { message: originalMessage });
    }
  }, [originalMessage, openModal]);

  const body = showBody ? (
    <div className="flex flex-col">
      <ComposeProviderStack
        formState={getFormStateValue(composeForm)}
        recipientsState={getRecipientsStateValue(composeForm)}
        clientState={getClientStateValue(composeForm)}
        emailFormatState={getEmailFormatStateValue()}
      >
        <ComposeFormTabs
          globalModalId={globalModalId}
          onSubmit={handleSubmitResolved}
          onCancel={() => handleOpenChange(false)}
          onGoToFullScreen={handleGoToFullScreen}
          showFullScreenButton={showFullScreenButton}
          onCreateTask={handleCreateTask}
          onCreateTicket={handleCreateTicket}
          originalMessage={originalMessage}
          isSending={isSending}
          emailConnections={composeForm.emailConnections}
          onTemplateSelect={composeForm.handleTemplateSelect}
          onRestoreFromHistory={() => {}}
          onClientSelect={composeForm.handleClientSelect}
          disclosure={disclosure}
        />
      </ComposeProviderStack>
    </div>
  ) : (
    <div className="min-h-[200px] animate-pulse" aria-busy="true" />
  );

  return (
    <>
      {renderShell({
        children: body,
        hasUnfilledVariables: showBody && composeForm.hasUnfilledVariables,
        bodyShown: showBody,
      })}
    </>
  );
});
